// src/components/themes/default/admin/BranchReviewsTab.tsx
"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import {
  Star,
  Search,
  Trash2,
  CornerDownRight,
  Send,
  Loader2,
  RefreshCw,
  Package,
  CheckCircle2,
  ShieldCheck,
  Building2,
  MessageSquare,
} from "lucide-react";

interface BranchReviewsTabProps {
  branchInfo: any;
  subdomain: string;
  theme?: "dark" | "light";
  primaryColor?: string;
}

export default function BranchReviewsTab({
  branchInfo,
  subdomain,
  theme = "dark",
  primaryColor = "#059669",
}: BranchReviewsTabProps) {
  const isLight = theme === "light";
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [starFilter, setStarFilter] = useState<string>("all");

  // Reply States
  const [replyingReviewId, setReplyingReviewId] = useState<number | null>(null);
  const [replyText, setReplyText] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);

  const branchId = branchInfo?.id;
  const branchName = branchInfo?.name || "Branch Outlet";

  const getBranchToken = useCallback(() => {
    if (typeof window === "undefined") return "";
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("branch_auth_")) {
          const data = JSON.parse(localStorage.getItem(key) || "{}");
          if (data?.token) return data.token;
        }
      }
    } catch {}
    return "";
  }, []);

  const getApiUrl = useCallback(
    (endpoint: string) => {
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1");
      return isLocal ? `http://${subdomain}.localhost:8000${endpoint}` : endpoint;
    },
    [subdomain]
  );

  // ১. ব্রাঞ্চের রিভিউগুলো ফেচ করা
  const fetchBranchReviews = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const token = getBranchToken();
      const res = await fetch(
        getApiUrl(`/api/v1/reviews/?branch_id=${branchId}&admin=true`),
        {
          headers: {
            Accept: "application/json",
            "X-Branch-ID": String(branchId),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.results || [];
        setReviews(list);
      }
    } catch (err) {
      console.error("Error fetching branch reviews:", err);
    } finally {
      setLoading(false);
    }
  }, [branchId, getApiUrl, getBranchToken]);

  useEffect(() => {
    fetchBranchReviews();
  }, [fetchBranchReviews]);

  // ২. রিভিউ ডিলিট করা
  const handleDeleteReview = async (reviewId: number) => {
    const result = await Swal.fire({
      title: "রিভিউটি ডিলিট করতে চান?",
      text: "একবার মুছে ফেললে এটি আর ফিরিয়ে আনা যাবে না!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e11d48",
      cancelButtonColor: "#64748b",
      confirmButtonText: "হ্যাঁ, ডিলিট করুন",
      cancelButtonText: "বাতিল",
      background: isLight ? "#ffffff" : "#0f172a",
      color: isLight ? "#0f172a" : "#ffffff",
    });

    if (!result.isConfirmed) return;

    try {
      const token = getBranchToken();
      const res = await fetch(getApiUrl(`/api/v1/reviews/${reviewId}/`), {
        method: "DELETE",
        headers: {
          "X-Branch-ID": String(branchId),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (res.ok || res.status === 204) {
        setReviews((prev) => prev.filter((r) => r.id !== reviewId));
        Swal.fire({
          icon: "success",
          title: "ডিলিট সম্পন্ন হয়েছে",
          toast: true,
          position: "top-end",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch {
      Swal.fire({ icon: "error", title: "ব্যর্থ হয়েছে" });
    }
  };

  // ৩. আউটলেট ম্যানেজার রিপ্লাই সাবমিট করা
  const handleSendReply = async (reviewId: number) => {
    if (!replyText.trim()) return;
    setSubmittingReply(true);

    try {
      const token = getBranchToken();
      const res = await fetch(getApiUrl("/api/v1/reviews/replies/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Branch-ID": String(branchId),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          review: reviewId,
          content: replyText.trim(),
        }),
      });

      if (res.ok) {
        const newReply = await res.json();
        setReviews((prev) =>
          prev.map((r) =>
            r.id === reviewId
              ? { ...r, replies: [...(r.replies || []), newReply] }
              : r
          )
        );
        setReplyingReviewId(null);
        setReplyText("");
        Swal.fire({
          icon: "success",
          title: "রিপ্লাই প্রদান করা হয়েছে",
          toast: true,
          position: "top-end",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch {
      Swal.fire({ icon: "error", title: "রিপ্লাই ব্যর্থ হয়েছে" });
    } finally {
      setSubmittingReply(false);
    }
  };

  // ফিল্টার্ড রিভিউ তালিকা
  const filteredReviews = useMemo(() => {
    return reviews.filter((r) => {
      const matchSearch =
        r.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.customer_display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.product_name?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchStar =
        starFilter === "all" || String(r.rating) === starFilter;

      return matchSearch && matchStar;
    });
  }, [reviews, searchQuery, starFilter]);

  // কেপিআই স্ট্যাটস
  const totalCount = reviews.length;
  const avgRating = totalCount
    ? (reviews.reduce((acc, r) => acc + (r.rating || 5), 0) / totalCount).toFixed(1)
    : "5.0";

  return (
    <div className="space-y-6 animate-in fade-in">
      {/* 🌟 Top Header Toolbar */}
      <div
        className={`p-6 rounded-3xl border-2 flex flex-wrap items-center justify-between gap-4 transition-all ${
          isLight
            ? "bg-white border-slate-200 text-slate-900 shadow-sm"
            : "bg-slate-900 border-slate-800 text-white shadow-xl"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            style={{
              backgroundColor: `${primaryColor}18`,
              color: primaryColor,
              borderColor: `${primaryColor}35`,
            }}
            className="w-12 h-12 rounded-2xl flex items-center justify-center border"
          >
            <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base sm:text-lg font-black tracking-tight">
                ★ Branch Product Reviews Management
              </h2>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 font-mono">
                Outlet Mode
              </span>
            </div>
            <p className={`text-xs ${isLight ? "text-slate-500" : "text-slate-400"}`}>
              {branchName} • Verified Customer Feedback ({totalCount} total reviews)
            </p>
          </div>
        </div>

        {/* Search & Star Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by customer, review or product..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-9 pr-3 py-2 rounded-xl text-xs outline-none border transition-all ${
                isLight
                  ? "bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400"
                  : "bg-slate-950 border-slate-800 text-white placeholder:text-slate-500"
              }`}
            />
          </div>

          <select
            value={starFilter}
            onChange={(e) => setStarFilter(e.target.value)}
            className={`px-3 py-2 rounded-xl text-xs font-bold border outline-none cursor-pointer ${
              isLight
                ? "bg-slate-50 border-slate-200 text-slate-800"
                : "bg-slate-950 border-slate-800 text-slate-200"
            }`}
          >
            <option value="all">All Stars</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>

          <button
            onClick={fetchBranchReviews}
            disabled={loading}
            className={`p-2 rounded-xl border transition-all cursor-pointer ${
              isLight
                ? "bg-slate-50 border-slate-200 hover:bg-slate-100"
                : "bg-slate-950 border-slate-800 hover:bg-slate-800"
            }`}
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* 🌟 Reviews Cards Feed */}
      {loading ? (
        <div className="py-24 text-center">
          <Loader2 style={{ color: primaryColor }} className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p className="text-xs font-bold text-slate-400">Loading branch reviews...</p>
        </div>
      ) : filteredReviews.length === 0 ? (
        <div
          className={`p-12 text-center rounded-3xl border-2 border-dashed space-y-3 ${
            isLight
              ? "bg-white border-slate-200 text-slate-500"
              : "bg-slate-900/60 border-slate-800 text-slate-400"
          }`}
        >
          <Package className="w-10 h-10 mx-auto text-slate-400 opacity-60" />
          <h4 className="text-sm font-black">কোনো ব্রাঞ্চ রিভিউ পাওয়া যায়নি</h4>
          <p className="text-xs">
            এই আউটলেটের প্রোডাক্টগুলোতে কাস্টমাররা রিভিউ দিলে তা এখানে জমা হবে।
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReviews.map((rev) => (
            <div
              key={rev.id}
              className={`p-5 sm:p-6 rounded-3xl border-2 transition-all space-y-3 ${
                isLight
                  ? "bg-white border-slate-200 shadow-xs"
                  : "bg-slate-900 border-slate-800 shadow-md"
              }`}
            >
              {/* Review Card Top Bar */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center font-black text-xs font-mono uppercase">
                    {rev.customer_display_name?.charAt(0) || "U"}
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <h4
                        className={`text-xs font-black ${
                          isLight ? "text-slate-900" : "text-white"
                        }`}
                      >
                        {rev.customer_display_name || "Anonymous Customer"}
                      </h4>

                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-blue-500/10 text-blue-500 border border-blue-500/20">
                        <Package className="w-2.5 h-2.5" />
                        <span>{rev.product_name || `Product #${rev.product}`}</span>
                      </span>
                    </div>

                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star
                            key={s}
                            className={`w-3 h-3 ${
                              s <= (rev.rating || 5)
                                ? "fill-amber-400 text-amber-400"
                                : "text-slate-600"
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono">
                        • {new Date(rev.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Delete Button */}
                <button
                  onClick={() => handleDeleteReview(rev.id)}
                  className="p-2 rounded-xl text-rose-500 hover:bg-rose-500/10 transition-colors cursor-pointer"
                  title="Delete Review"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Review Text */}
              <div className="pl-12 space-y-1">
                {rev.title && (
                  <h5
                    className={`text-xs font-black ${
                      isLight ? "text-slate-900" : "text-white"
                    }`}
                  >
                    {rev.title}
                  </h5>
                )}
                <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
                  {rev.content}
                </p>
              </div>

              {/* Existing Replies */}
              {rev.replies && rev.replies.length > 0 && (
                <div className="ml-12 space-y-2 pt-1">
                  {rev.replies.map((reply: any, rIdx: number) => (
                    <div
                      key={rIdx}
                      className={`p-3 rounded-2xl border text-xs space-y-1 ${
                        isLight
                          ? "bg-slate-50 border-slate-200"
                          : "bg-slate-950/70 border-slate-800"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 font-bold text-emerald-500 text-[11px]">
                        <CornerDownRight className="w-3.5 h-3.5" />
                        <span>{reply.user_name || "Outlet Store Manager"}</span>
                        <span className="text-[10px] text-slate-400 font-mono font-normal">
                          • {new Date(reply.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="pl-5 text-slate-700 dark:text-slate-300">
                        {reply.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply Button & Form */}
              <div className="pl-12 pt-1">
                {replyingReviewId === rev.id ? (
                  <div className="space-y-2 mt-2">
                    <textarea
                      rows={2}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={`Reply as ${branchName} manager...`}
                      className={`w-full p-3 rounded-xl border text-xs outline-none ${
                        isLight
                          ? "bg-white border-slate-300 text-slate-800"
                          : "bg-slate-950 border-slate-700 text-white"
                      }`}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSendReply(rev.id)}
                        disabled={submittingReply || !replyText.trim()}
                        style={{ backgroundColor: primaryColor }}
                        className="px-4 py-1.5 rounded-xl text-white text-xs font-bold inline-flex items-center gap-1.5 shadow-md cursor-pointer hover:opacity-90 disabled:opacity-50"
                      >
                        {submittingReply ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Send className="w-3.5 h-3.5" />
                        )}
                        <span>Send Reply</span>
                      </button>

                      <button
                        onClick={() => {
                          setReplyingReviewId(null);
                          setReplyText("");
                        }}
                        className="px-3 py-1.5 rounded-xl text-xs font-bold text-slate-400 hover:text-slate-200"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setReplyingReviewId(rev.id);
                      setReplyText("");
                    }}
                    className="text-xs font-bold text-slate-400 hover:text-emerald-500 inline-flex items-center gap-1 transition-colors cursor-pointer"
                  >
                    <CornerDownRight className="w-3.5 h-3.5" />
                    <span>Reply to Customer</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}